import {
  ContextSelector,
  createContext,
  useContextSelector,
} from '@fluentui/react-context-selector';
import isSvg from 'is-svg';
import { isEqual, uniq } from 'lodash';
import { Dispatch, useCallback, useEffect, useMemo } from 'react';

import theme from '../assets/theme';
import {
  LOADING_SLIDE,
  LOADING_SLIDE_ID,
} from '../components/_molecules/DesignerSlideList/loadingSlide';
import {
  createGradeDropdownPromptInput,
  createNewImagePrompt,
  createNewPrompt,
  createPromptUniqueId,
  NewPromptTemplate,
} from '../components/_organisms/DesignerTabPane/PromptsTab/newPromptTemplates';
import {
  createSimpleSlateParagraphFromAIOutput,
  findAllSlateTextColors,
  getFirstSlateElementPropsFromSlateString,
  isCuripodText,
  parseStringToSlateObject,
  updatePromptInputElementLabelById,
  updatePromptOutputElementLabelById,
} from '../components/SlideComponents/slateUtil';
import {
  MediaMimeType,
  OnElementsChangeProps,
  PollOptions,
  Slide,
  SlideContentType,
  SlideElement,
  SlideType,
} from '../components/SlideComponents/slide.types';
import {
  colorIsHexCode,
  convertToCuripodSvg,
  createFigureElement,
  createFixedTextElement,
  createImageElement,
  createNewEmptySlide,
  createPollElements,
  createSlideGroups,
  findRelatedSlidesWithIndex,
  findRelatedSlidesWithoutIndex,
  generateUniqueId,
  insertSlidesAfterSelectedSlide,
  insertSlidesBeforeSelectedSlide,
  isEmptySlide,
  isSlideElementText,
  SLIDE_ASPECT,
} from '../components/SlideComponents/slideUtil';
import {
  copyElement,
  copySlide,
  makeNewIdsForSlide,
  parseClipboardData,
  retrieveClipBoardImage,
  retrieveClipBoardText,
} from '../components/SlideComponents/slideUtilCopyPaste';
import { useHideIntercom } from '../providers/CustomIntercomStateProvider';
import BackendService, {
  LessonAttachment,
} from '../services/backendService/backendService';
import {
  ActiveGame,
  ById,
  Prompt,
  PromptInput,
  PromptMessageRole,
  PromptOutput,
  PromptOutputProperty,
  RoundMedia,
} from '../services/backendService/types';
import { AiContent, Template } from '../services/TemplateService/Template.type';
import { exportPdf } from '../utils/exportPdf';
import { readImageFiles } from '../utils/fileDropBrowserUtils';
import { notUndefined } from '../utils/general';
import { generateRoundOptionColor, isSlideType } from '../utils/roundUtils';
import { MAX_POLL_OPTIONS } from '../utils/templateUtils';
import { NEW_TEMPLATE_PARAM } from '../utils/urlUtils';
import useAccessBadge from './useAccessBadge';
import {
  DesignerUIStateAction,
  DesignerUIStateActions,
  setSelectedTab,
  TabState,
  useDesignerUIContext,
} from './useDesignerUIState';
import { useGameMediaFileUploader } from './useGameMediaFileUploader';
import { useMetrics } from './useMetrics';
import useMixpanelFlag from './useMixpanelFlag';
import { useReducerWithMiddleware } from './useReducerWithMiddlewares';
import useRouterMatch from './useRouterMatch';
import { useRawTemplate } from './useTemplate';
import { useTemplateAccessHandler } from './useTemplateAccessHandler';
import useUndoRedo from './useUndoRedo';

export type GeneratorCategory = {
  name: string;
  value: string;
};

const createCurifyPlaceholderSlides = (
  promptInput: PromptInput,
  slides: Slide[],
): Slide[] => {
  // Create a list of many slides up to the minLength of the promptInput
  const slidesLength = promptInput.constraints?.step;

  //TODO support keeping the slide indexes with some edge cases
  const normalSlides = slides.filter(slide => !slide.metadata?.isCurifyPlaceholder);

  const newPlaceholderSlides = Array.from(Array(slidesLength)).map((_, i) => {
    const newElement = createFixedTextElement({
      fontSize: 80,
      y: 455,
      width: SLIDE_ASPECT.width * 0.9,
      textValue: `Curify placeholder slide ${i}`,
      textColor: theme.colors.white,
      lockedFromDeletion: true,
      lockedPosition: true,
    });
    const newElement2 = createFixedTextElement({
      fontSize: 42,
      y: 800,
      width: SLIDE_ASPECT.width * 0.9,
      textValue: `This slide will be replaced during generation. Delete the whole slide to prevent side effects. You can duplicate if you want more placeholders.`,
      textColor: theme.colors.white,
      lockedFromDeletion: true,
      lockedPosition: true,
    });
    const newSlide = createNewEmptySlide({
      contentType: 'fullscreen_media',
      fullScreenEmpty: true,
      type: 'slide',
      backgroundMedia: {
        fileId: '#000000',
        fileMimeType: 'image/color',
      },
      elements: [newElement, newElement2],
    });
    newSlide.metadata = {
      isCurifyPlaceholder: true,
    };
    return newSlide;
  });

  return [...normalSlides, ...newPlaceholderSlides];
};

export const allPromptOutputsSelector = (
  context: DesignerDataStateContextType,
): Array<PromptOutput & { promptName: string; promptId: string }> => {
  const prompts = context.state.localTemplate?.aiContent?.prompts || [];

  return prompts.map(p => ({ ...p.promptOutput, promptName: p.name, promptId: p.id }));
};

export const emptyAIContent: AiContent = {
  categories: [],
  promptInputs: [
    createGradeDropdownPromptInput(),
    {
      id: `input-topic`,
      label: 'Topic',
      order: 0,
      type: 'shortText',
      allowEmpty: false,
      isRequired: true,
    },
  ],
  prompts: [],
  promptInputSuggestions: [],
};
export const isOngoingGameModalVisibleSelector = (
  context: DesignerDataStateContextType,
) => !!context.state.localTemplate?.content.activeGame?.isOngoing;

export const elementsToRenderSelector = (context: DesignerDataStateContextType) => {
  const selectedSlide = selectedSlideSelector(context);
  if (!selectedSlide) return [];
  if (selectedSlide.type === 'poll') {
    const pollElements = createPollElements({
      pollOptions: selectedSlide.pollOptions || [],
      aspect: SLIDE_ASPECT,
    });
    return [...selectedSlide.elements, ...pollElements];
  }
  return selectedSlide.elements;
};

export const selectedSlideIdSelector = (context: DesignerDataStateContextType) => {
  return context.state.localTemplate?.currentRound;
};

export const attachmentsSelector = (context: DesignerDataStateContextType) => {
  return context.state.localTemplate?.attachments;
};
export const presentationIdSelector = (context: DesignerDataStateContextType) => {
  return context.state.localTemplate?.id;
};

export const selectedSlideSelector = (context: DesignerDataStateContextType) => {
  const localTemplate = context.state.localTemplate;
  if (
    localTemplate?.content.slides &&
    localTemplate?.content.slides.length > 0 &&
    localTemplate?.currentRound
  ) {
    return localTemplate?.content.slides.find(r => r.id === localTemplate?.currentRound);
  }
  return undefined;
};

export const documentColorsSelector = (context: DesignerDataStateContextType) => {
  const localTemplate = context.state.localTemplate;

  if (!localTemplate?.content.slides) {
    return [];
  }
  const colors = localTemplate.content.slides.reduce((acc, slide) => {
    const slideColors = slide.elements.reduce((acc, element) => {
      if (element.type === 'svg' && element.colors) {
        return [...acc, ...element.colors];
      }
      if (element.type === 'shape' && element.colors) {
        return [...acc, ...element.colors];
      }

      if (isSlideElementText(element)) {
        return [...acc, ...findAllSlateTextColors(element.textValue)];
      }

      return acc;
    }, [] as string[]);

    // adds the slide background color if it's a color
    if (slide.backgroundMedia?.fileMimeType === 'image/color') {
      return [...acc, ...slideColors, slide.backgroundMedia.fileId];
    }

    return [...acc, ...slideColors];
  }, [] as string[]);
  return uniq(colors).filter(colorIsHexCode);
};

function duplicateSlide(localTemplate: TemplateState, slideId: string): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;

  const slide = localTemplate.content.slides.find(s => s.id === slideId);
  if (!slide) return localTemplate;

  let newSlides: Slide[] = [];
  if (slide.type === 'slide') {
    const newSlide: Slide = { ...slide, id: generateUniqueId().toString() };
    newSlides = [newSlide];
  } else {
    const relatedSlides = findRelatedSlidesWithoutIndex(
      slide.id,
      localTemplate?.content.slides,
    );
    newSlides = makeNewIdsForSlide(relatedSlides);
  }
  return {
    ...localTemplate,
    content: {
      ...localTemplate.content,
      slides: insertSlidesAfterSelectedSlide(
        localTemplate.content.slides,
        newSlides,
        slide.id,
      ),
    },
    currentRound: newSlides[0].id,
  };
}

function previousSlide(localTemplate: TemplateState): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;
  const selectedSlide = localTemplate.content.slides.find(
    s => s.id === localTemplate.currentRound,
  );
  if (!selectedSlide) {
    return localTemplate;
  }
  const currentSlideIndex = localTemplate.content.slides.findIndex(
    r => r.id === selectedSlide?.id,
  );
  if (currentSlideIndex === -1) {
    return {
      ...localTemplate,
      currentRound: localTemplate.content.slides[0].id,
    };
  }

  if (currentSlideIndex === 0) {
    return localTemplate;
  }

  const nextSlide = localTemplate.content.slides[currentSlideIndex - 1];
  if (!nextSlide) {
    return localTemplate;
  }
  const relatedSlides = findRelatedSlidesWithoutIndex(
    nextSlide.id,
    localTemplate.content.slides,
  );
  // Select the next round in the group array
  return { ...localTemplate, currentRound: relatedSlides[0].id };
}

function nextSlide(localTemplate: TemplateState): TemplateState {
  if (!localTemplate || !localTemplate.content.slides) return localTemplate;
  const selectedSlide = localTemplate.content.slides.find(
    s => s.id === localTemplate.currentRound,
  );
  if (!selectedSlide) {
    return localTemplate;
  }
  const currentSlideIndex = localTemplate.content.slides.findIndex(
    r => r.id === selectedSlide?.id,
  );
  if (currentSlideIndex === -1) {
    const newTemplate = {
      ...localTemplate,
      currentRound: localTemplate.content.slides[0].id,
    };
    return newTemplate;
  }
  if (currentSlideIndex === localTemplate.content.slides.length - 1) {
    // If you are already at the last round
    return localTemplate;
  }

  const currentRelatedSlides = findRelatedSlidesWithIndex(
    selectedSlide.id,
    localTemplate.content.slides,
  );
  const nextRound =
    localTemplate.content.slides[
      currentRelatedSlides[currentRelatedSlides.length - 1].index
    ];
  if (!nextRound) {
    return localTemplate;
  }
  const relatedSlides = findRelatedSlidesWithoutIndex(
    nextRound.id,
    localTemplate.content.slides,
  );
  // Select the next round in the group array
  return { ...localTemplate, currentRound: relatedSlides[0].id };
}

function deleteSlide(localTemplate: TemplateState, slideId: string): TemplateState {
  if (!localTemplate) return localTemplate;
  if (!localTemplate.content.slides) return localTemplate;
  if (!localTemplate.currentRound) return localTemplate;

  const relatedSlides = findRelatedSlidesWithoutIndex(
    slideId,
    localTemplate?.content.slides,
  );

  // metrics.logEvent('Creator.DeleteRound.Open');

  const index = localTemplate.content.slides.findIndex(r => r.id === relatedSlides[0].id);

  let newSelectedSlideId: string | null = null;
  if (index === 0) {
    newSelectedSlideId = localTemplate.content.slides[0].id;
  } else {
    const nextSelectedSlide = localTemplate.content.slides[index - 1];
    const nextRelatedSlides = findRelatedSlidesWithoutIndex(
      nextSelectedSlide.id,
      localTemplate?.content.slides,
    );
    newSelectedSlideId = nextRelatedSlides[0].id;
  }

  const ids = relatedSlides.map(r => r.id);
  const newSlides = (localTemplate.content.slides || []).filter(r =>
    ids.includes(r.id) ? false : true,
  );
  const templateState = {
    ...localTemplate,
    content: {
      ...localTemplate.content,
      slides: newSlides,
    },
    currentRound: newSelectedSlideId,
  };
  return templateState;
}

export enum TemplateStateActions {
  init = 'INIT',
  changeSelectedSlide = 'CHANGE_SELECTED_SLIDE',
  deleteSlide = 'DELETE_SLIDE',
  initActiveGame = 'INIT_ACTIVE_GAME',
  initSetFirstSlide = 'INIT_SET_FIRST_SLIDE',
  nextSlide = 'NEXT_SLIDE',
  previousSlide = 'PREVIOUS_SLIDE',
  copySlide = 'COPY_SLIDE',
  endGame = 'END_GAME',
  changeSlide = 'CHANGE_SLIDE',
  addSlide = 'ADD_SLIDE',
  addTranslatedSlides = 'ADD_TRANSLATED_SLIDES',
  slidesDragged = 'SLIDES_DRAGGED',
  allSlidesChanged = 'ALL_SLIDES_CHANGED',
  agentUpdatedState = 'AGENT_UPDATED_STATE',
  setBackgroundOnAllSlides = 'SET_BACKGROUND_ON_ALL_SLIDES',
  titleChanged = 'TITLE_CHANGED',
  descriptionChanged = 'DESCRIPTION_CHANGED',
  coverImageAltTextChanged = 'COVER_IMAGE_ALT_TEXT_CHANGED',
  languageChanged = 'LANGUAGE_CHANGED',
  parentIdChanged = 'PARENT_ID_CHANGED',
  changeSubjects = 'CHANGE_SUBJECTS',
  changeGrades = 'CHANGE_GRADES',
  realNamesChanged = 'REAL_NAMES_CHANGED',
  contentFilteringChanged = 'CONTENT_FILTERING_CHANGED',
  coverPhotoChanged = 'COVER_PHOTO_CHANGED',
  sharingPublicChanged = 'SHARE_PUBLIC_CHANGED',
  isBlockedFromSharingChanged = 'IS_BLOCKED_FROM_SHARING_CHANGED',
  // Prompt editing
  addPrompt = 'ADD_PROMPT',
  addImagePrompt = 'ADD_IMAGE_PROMPT',
  editPrompt = 'EDIT_PROMPT',
  duplicatePrompt = 'DUPLICATE_PROMPT',
  deletePrompt = 'DELETE_PROMPT',
  addPromptInput = 'ADD_PROMPT_INPUT',
  deletePromptInput = 'DELETE_PROMPT_INPUT',
  editPromptInput = 'EDIT_PROMPT_INPUT',
  changePromptMessageRole = 'CHANGE_PROMPT_MESSAGE_ROLE',
  changePromptOutputType = 'CHANGE_PROMPT_OUTPUT_TYPE',
  addPromptOutputProperty = 'ADD_PROMPT_OUTPUT_PROPERTY',
  addTestCase = 'ADD_TEST_CASE',
  editPromptOutputProperty = 'EDIT_PROMPT_OUTPUT_PROPERTY',
  deletePromptOutputProperty = 'DELETE_PROMPT_OUTPUT_PROPERTY',
  convertTextElementToAITextElement = 'CONVERT_TEXT_ELEMENT_TO_AI_TEXT_ELEMENT',
  convertImageElementToAIImageElement = 'CONVERT_IMAGE_ELEMENT_TO_AI_IMAGE_ELEMENT',
  editGeneratorCategories = 'EDIT_GENERATOR_CATEGORIES',
  addCurifyPlaceholderSlides = 'ADD_CURIFY_PLACEHOLDER_SLIDES',
  attachmentCreated = 'ATTACHMENT_CREATED',
  attachmentUpserted = 'ATTACHMENT_UPSERTED',
  attachmentEdited = 'ATTACHMENT_EDITED',
  attachmentDeleted = 'ATTACHMENT_DELETED',
  metadataPropertyChanged = 'METADATA_PROPERTY_CHANGED',
  slideVariationDesignation = 'SLIDE_VARIATION_DESIGNATION',
}

export type TemplateAction = { skipHistory?: boolean } & (
  | { type: TemplateStateActions.init; template: Template }
  | { type: TemplateStateActions.changeSelectedSlide; slideId: string }
  | { type: TemplateStateActions.initActiveGame; activeGame: ActiveGame }
  | { type: TemplateStateActions.initSetFirstSlide }
  | { type: TemplateStateActions.nextSlide }
  | { type: TemplateStateActions.previousSlide }
  | { type: TemplateStateActions.endGame }
  | { type: TemplateStateActions.addSlide; slides: Slide[]; replace: boolean }
  | {
      type: TemplateStateActions.addTranslatedSlides;
      slides: Slide[];
      lessonAttachments: LessonAttachment[];
      roundsToTranslate: {
        name: string;
        slideId: string;
      }[];
    }
  | { type: TemplateStateActions.changeSlide; slide: Slide }
  | {
      type: TemplateStateActions.allSlidesChanged;
      slides: Slide[];
      selectedSlideId: string;
    }
  | {
      type: TemplateStateActions.agentUpdatedState;
      slides: Slide[];
      selectedSlideId: string;
      attachments: LessonAttachment[];
      metadata: ById<string | number | boolean> | null | undefined;
      keepCurrentSlide?: boolean;
    }
  | { type: TemplateStateActions.copySlide; slideId: string }
  | { type: TemplateStateActions.slidesDragged; slides: Slide[] }
  | { type: TemplateStateActions.setBackgroundOnAllSlides; media: RoundMedia }
  | { type: TemplateStateActions.titleChanged; value?: string }
  | { type: TemplateStateActions.descriptionChanged; value?: string }
  | { type: TemplateStateActions.coverImageAltTextChanged; value?: string }
  | { type: TemplateStateActions.languageChanged; language: string }
  | { type: TemplateStateActions.parentIdChanged; parentId: string | null }
  | { type: TemplateStateActions.realNamesChanged; require: boolean }
  | { type: TemplateStateActions.contentFilteringChanged; isEnabled: boolean }
  | { type: TemplateStateActions.sharingPublicChanged; templateSharingPublic: boolean }
  | {
      type: TemplateStateActions.metadataPropertyChanged;
      payload: {
        key: string;
        value: string | number | boolean;
      };
    }
  | {
      type: TemplateStateActions.isBlockedFromSharingChanged;
      isBlockedFromSharing: boolean;
    }
  | {
      type: TemplateStateActions.coverPhotoChanged;
      coverImageId: string;
      coverImageAltText?: string;
    }
  | { type: TemplateStateActions.deleteSlide; slideId: string }
  | {
      type: TemplateStateActions.addPrompt;
      promptName: string;
      newPromptId: string;
      newPromptTemplate?: NewPromptTemplate;
    }
  | {
      type: TemplateStateActions.addImagePrompt;
      promptName: string;
      newPromptId: string;
      newPromptTemplate?: NewPromptTemplate;
    }
  | { type: TemplateStateActions.deletePrompt; promptId: string }
  | { type: TemplateStateActions.duplicatePrompt; promptId: string }
  | { type: TemplateStateActions.editPrompt; prompt: Prompt }
  | { type: TemplateStateActions.addPromptInput; promptInput?: PromptInput }
  | { type: TemplateStateActions.editPromptInput; promptInput: PromptInput }
  | { type: TemplateStateActions.deletePromptInput; inputId: string }
  | {
      type: TemplateStateActions.changePromptMessageRole;
      role: PromptMessageRole;
      messageIndex: number;
    }
  | {
      type: TemplateStateActions.changePromptOutputType;
      outputType: 'string' | 'object' | 'array';
      promptId: string;
    }
  | {
      type: TemplateStateActions.addPromptOutputProperty;
      promptId: string;
    }
  | {
      type: TemplateStateActions.editPromptOutputProperty;
      promptId: string;
      promptOutputProperty: PromptOutputProperty;
      promptOutputPropertyOrder?: string[];
    }
  | {
      type: TemplateStateActions.deletePromptOutputProperty;
      promptId: string;
      promptOutputPropertyId: string;
    }
  | {
      type: TemplateStateActions.convertTextElementToAITextElement;
      elementId: string;
      promptSourceId: string;
      exampleText: string;
      isPollOption?: boolean;
    }
  | {
      type: TemplateStateActions.convertImageElementToAIImageElement;
      elementId: string;
      promptImageSourceId: string;
      exampleImage: {
        id?: string;
        mimetype: MediaMimeType;
        url?: string;
        thumbnail?: string;
        downloadLocation?: string;
      };
    }
  | {
      type: TemplateStateActions.editGeneratorCategories;
      categories: GeneratorCategory[];
    }
  | {
      type: TemplateStateActions.changeSubjects;
      subjects: string[];
    }
  | {
      type: TemplateStateActions.changeGrades;
      grades: string[];
    }
  | {
      type: TemplateStateActions.addCurifyPlaceholderSlides;
      promptInputId: string;
    }
  | {
      type: TemplateStateActions.attachmentCreated;
      attachment: LessonAttachment;
    }
  | {
      type: TemplateStateActions.attachmentUpserted;
      attachment: LessonAttachment;
    }
  | {
      type: TemplateStateActions.attachmentEdited;
      attachment: LessonAttachment;
    }
  | {
      type: TemplateStateActions.attachmentDeleted;
      attachmentId: string;
    }
);

export type TemplateState = Template | undefined;

// Helper function to update attachment IDs in Slate nodes

function updateSlateNodeAttachmentIds(
  node: any,
  attachmentIdMap: Map<string, string>,
): any {
  if (!node || typeof node !== 'object') return node;

  // If this is a livePromptInput node with an inputId that matches an attachment
  if (node.type === 'livePromptInput' && node.inputId) {
    const newAttachmentId = attachmentIdMap.get(node.inputId);
    if (newAttachmentId) {
      return {
        ...node,
        inputId: newAttachmentId,
        children: node.children?.map((child: any) =>
          updateSlateNodeAttachmentIds(child, attachmentIdMap),
        ),
      };
    }
  }

  // Recursively update children for all other nodes
  if (node.children && Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map((child: any) =>
        updateSlateNodeAttachmentIds(child, attachmentIdMap),
      ),
    };
  }

  return node;
}

export function templateReducer(
  state: TemplateState,
  action: TemplateAction,
): TemplateState {
  const { type } = action;
  let newState: TemplateState = undefined;
  switch (type) {
    case TemplateStateActions.init: {
      newState = action.template;
      break;
    }
    case TemplateStateActions.changeSelectedSlide: {
      if (!state) return state;
      const exists = state?.content.slides?.some(s => s.id === action.slideId);
      if (!exists) return state; //TODO throw error?
      newState = {
        ...state,
        currentRound: action.slideId,
      };
      break;
    }
    case TemplateStateActions.deleteSlide: {
      if (!state) return state;
      newState = deleteSlide(state, action.slideId);
      break;
    }
    case TemplateStateActions.initActiveGame: {
      if (!state) return state;
      newState = {
        ...state,
        content: {
          ...state?.content,
          activeGame: action.activeGame,
        },
      };
      break;
    }
    case TemplateStateActions.initSetFirstSlide: {
      if (!state) return state;
      newState = {
        ...state,
        currentRound: state?.content?.slides?.[0]?.id,
      };
      break;
    }
    case TemplateStateActions.nextSlide: {
      if (!state) return state;
      newState = nextSlide(state);
      break;
    }
    case TemplateStateActions.previousSlide: {
      if (!state) return state;
      newState = previousSlide(state);
      break;
    }
    case TemplateStateActions.copySlide: {
      if (!state) return state;
      newState = duplicateSlide(state, action.slideId);
      break;
    }
    case TemplateStateActions.endGame: {
      if (!state) return state;
      newState = {
        ...state,
        content: {
          ...state.content,
          activeGame: null,
        },
      };
      break;
    }
    case TemplateStateActions.changeSlide: {
      if (!state) return state;
      const slides = [...(state.content.slides || [])];

      const newSlides = slides
        ? slides.map(s => (s.id === action.slide.id ? action.slide : s))
        : null;
      newState = {
        ...state,
        content: { ...state.content, slides: newSlides },
      };
      break;
    }
    case TemplateStateActions.addSlide: {
      if (!state) return state;
      if (!action.slides) return state;
      if (action.slides.length === 0) return state;
      const selectedSlide = state.content.slides?.find(s => s.id === state.currentRound);
      const existingSlides = [...(state.content.slides || [])];
      let slides = insertSlidesAfterSelectedSlide(
        existingSlides,
        action.slides,
        selectedSlide?.id,
      );

      // replace and not add if selected slide is empty
      if (action.replace && selectedSlide) {
        const relatedSlides = findRelatedSlidesWithoutIndex(selectedSlide.id, slides).map(
          s => s.id,
        );
        slides = slides.filter(s => !relatedSlides.includes(s.id));
      }
      newState = {
        ...state,
        currentRound: action.slides[0].id,
        content: {
          ...state.content,
          slides,
        },
      };
      break;
    }
    case TemplateStateActions.addTranslatedSlides: {
      if (!state) return state;
      if (!action.slides) return state;
      if (action.slides.length === 0) return state;
      const slidesToBeTranslated = action.roundsToTranslate;

      const existingSlides = [...(state.content.slides || [])];
      const newTranslatedSlides = action.slides;
      let newSelectedSlide;
      const relatedSlides: Record<string, Slide[]> = {};
      //Group each of the related translated slides by the id of the original slide
      slidesToBeTranslated.forEach((slide, i) => {
        relatedSlides[slide.slideId] = makeNewIdsForSlide(
          findRelatedSlidesWithoutIndex(slide.slideId, newTranslatedSlides).map(s => s),
        );
        if (i === 0) newSelectedSlide = relatedSlides[slide.slideId][0].id;
      });

      let slides: Slide[] = [];
      slidesToBeTranslated.forEach(slide => {
        const currentRelatedSlides = findRelatedSlidesWithoutIndex(
          slide.slideId,
          existingSlides,
        );
        const firstRelatedSlide = currentRelatedSlides[0];
        const firstRelatedSlideId = existingSlides.find(
          slide => slide.id === firstRelatedSlide.id,
        )?.id;
        if (!firstRelatedSlideId) return;
        slides = insertSlidesBeforeSelectedSlide(
          slides.length === 0 ? existingSlides : slides,
          relatedSlides[slide.slideId],
          firstRelatedSlideId,
        );
      });

      if (
        state.attachments &&
        action.lessonAttachments &&
        action.lessonAttachments.length > 0
      ) {
        newState = {
          ...state,
          currentRound: newSelectedSlide,
          content: {
            ...state.content,
            slides,
          },
          attachments: [...state.attachments, ...action.lessonAttachments],
        };
        break;
      }

      newState = {
        ...state,
        currentRound: newSelectedSlide,
        content: {
          ...state.content,
          slides,
        },
      };
      break;
    }

    case TemplateStateActions.slidesDragged: {
      if (!state) return state;
      newState = {
        ...state,
        content: {
          ...state.content,
          slides: action.slides,
        },
      };
      break;
    }
    case TemplateStateActions.allSlidesChanged: {
      if (!state) return state;
      newState = {
        ...state,
        content: {
          ...state.content,
          slides: action.slides,
        },
        currentRound: action.selectedSlideId,
      };
      break;
    }
    case TemplateStateActions.agentUpdatedState: {
      if (!state) return state;

      // When keep current slide is true we are streaming in new slides.
      // We want to only force the user to select the first activity we generate.
      // This is the second slide when we get the first set of slides from the backend.
      // So we only set the current round in this case.

      let currentRound = action.keepCurrentSlide
        ? state.currentRound
        : action.selectedSlideId;

      if (action.keepCurrentSlide && !state.currentRound && action.slides.length > 0) {
        const slideGroups = createSlideGroups(action.slides);

        const firstSlide = slideGroups[0][0];
        currentRound = firstSlide.id;
      }

      // Keep LOADING_SLIDE when:
      // 1. It currently exists in state
      // 2. New slides don't include it
      // 3. No new slides yet OR we want to preserve it during updates
      // The loading slide stays in the list even if user navigates away from it
      // It will be removed once the generation is complete and new slides replace it
      const currentLoadingSlide = state.content.slides?.find(
        s => s.id === LOADING_SLIDE_ID,
      );
      const hasLoadingSlideInNewSlides = action.slides.some(
        s => s.id === LOADING_SLIDE_ID,
      );
      const shouldPreserveLoadingSlide =
        currentLoadingSlide && !hasLoadingSlideInNewSlides;
      const mergedSlides = shouldPreserveLoadingSlide
        ? [currentLoadingSlide, ...action.slides]
        : action.slides;

      newState = {
        ...state,
        content: {
          ...state.content,
          slides: mergedSlides,
        },
        currentRound,
        attachments: action.attachments,
        metadata: action.metadata,
      };
      break;
    }
    case TemplateStateActions.setBackgroundOnAllSlides: {
      if (!state) return state;
      newState = {
        ...state,
        content: {
          ...state.content,
          slides: (state?.content?.slides || []).map(r => ({
            ...r,
            backgroundMedia: action.media,
          })),
        },
      };
      break;
    }
    case TemplateStateActions.titleChanged: {
      if (!state) return state;
      newState = {
        ...state,
        title: action.value,
      };
      break;
    }
    case TemplateStateActions.descriptionChanged: {
      if (!state) return state;
      newState = {
        ...state,
        description: action.value,
      };
      break;
    }
    case TemplateStateActions.coverImageAltTextChanged: {
      if (!state) return state;
      newState = {
        ...state,
        coverImageAltText: action.value,
      };
      break;
    }
    case TemplateStateActions.languageChanged: {
      if (!state) return state;
      newState = {
        ...state,
        language: action.language,
      };
      break;
    }
    case TemplateStateActions.parentIdChanged: {
      if (!state) return state;
      newState = {
        ...state,
        parentId: action.parentId,
      };
      break;
    }
    case TemplateStateActions.contentFilteringChanged: {
      if (!state) return state;
      newState = {
        ...state,
        isContentFilterEnabled: action.isEnabled,
      };
      break;
    }
    case TemplateStateActions.realNamesChanged: {
      if (!state) return state;
      newState = {
        ...state,
        requireRealNames: action.require,
      };
      break;
    }
    case TemplateStateActions.coverPhotoChanged: {
      if (!state) return state;
      newState = {
        ...state,
        coverImageId: action.coverImageId,
        coverImageAltText: action.coverImageAltText,
      };
      break;
    }
    case TemplateStateActions.sharingPublicChanged: {
      if (!state) return state;
      newState = {
        ...state,
        templateSharingPublic: action.templateSharingPublic,
      };
      break;
    }
    case TemplateStateActions.metadataPropertyChanged: {
      if (!state) return state;
      newState = {
        ...state,
        metadata: { ...state.metadata, [action.payload.key]: action.payload.value },
      };
      break;
    }
    case TemplateStateActions.isBlockedFromSharingChanged: {
      if (!state) return state;
      newState = {
        ...state,
        isBlockedFromSharing: action.isBlockedFromSharing,
      };
      break;
    }
    case TemplateStateActions.addPrompt: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            createNewPrompt(
              action.newPromptId,
              action.promptName,
              action.newPromptTemplate,
            ),
          ],
        },
      };
      break;
    }
    case TemplateStateActions.addImagePrompt: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            createNewImagePrompt(
              action.newPromptId,
              action.promptName,
              action.newPromptTemplate,
            ),
          ],
        },
      };
      break;
    }
    case TemplateStateActions.deletePrompt: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: state.aiContent?.prompts?.filter(p => p.id !== action.promptId) || [],
        },
      };
      break;
    }
    case TemplateStateActions.duplicatePrompt: {
      if (!state) return state;
      const prompt = state.aiContent?.prompts?.find(p => p.id === action.promptId);
      if (!prompt) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: [
            ...(state.aiContent?.prompts || []),
            { ...prompt, id: createPromptUniqueId(), name: `${prompt.name} (copy)` },
          ],
        },
      };
      break;
    }
    case TemplateStateActions.editPrompt: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || [])?.map(eachPrompt => {
            if (eachPrompt.id === action.prompt.id) {
              return action.prompt;
            }
            // on prompt edit, we need to update the label of this particular prompt in other prompt children nodes where this was embedded
            return updatePromptOutputElementLabelById({
              prompt: eachPrompt,
              id: action.prompt.id,
              updatedLabel: action.prompt.name,
            });
          }),
        },
      };
      break;
    }

    case TemplateStateActions.addPromptInput: {
      if (!state) return state;
      const newPromptInput = action.promptInput || {
        id: `input-${generateUniqueId()}`,
        label: 'New input',
        order: state.aiContent?.promptInputs?.length || 0,
        type: 'shortText',
        allowEmpty: false,
        isRequired: true,
      };

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: [...(state.aiContent?.promptInputs || []), newPromptInput],
        },
      };
      break;
    }
    case TemplateStateActions.deletePromptInput: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: (state.aiContent?.promptInputs || []).filter(
            input => input.id !== action.inputId,
          ),
        },
      };
      break;
    }
    case TemplateStateActions.editPromptInput: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          promptInputs: (state.aiContent?.promptInputs || []).map(input =>
            input.id === action.promptInput.id ? action.promptInput : input,
          ),
          prompts: (state.aiContent?.prompts || []).map(eachPrompt =>
            // on input edit, we need to update the label of this particular input in other prompt children nodes where this was embedded
            updatePromptInputElementLabelById({
              prompt: eachPrompt,
              id: action.promptInput.id,
              updatedLabel: action.promptInput.label,
            }),
          ),
        },
      };
      break;
    }
    case TemplateStateActions.changePromptMessageRole: {
      if (!state) return state;
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => ({
            ...p,
            messages: p.messages.map((m, i) =>
              i === action.messageIndex ? { ...m, role: action.role } : m,
            ),
          })),
        },
      };
      break;
    }
    case TemplateStateActions.changePromptOutputType: {
      if (!state) return state;
      const newId = `output-${generateUniqueId()}`;
      const newOutputProperty: ById<PromptOutputProperty> = {};
      if (action.outputType === 'object') {
        newOutputProperty[newId] = {
          description: '',
          id: newId,
          type: 'string',
        };
      }
      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p =>
            p.id === action.promptId
              ? {
                  ...p,
                  promptOutput: {
                    ...p.promptOutput,
                    type: action.outputType,
                    properties: newOutputProperty,
                    propertiesOrder: action.outputType !== 'string' ? [newId] : undefined,
                  },
                }
              : p,
          ),
        },
      };
      break;
    }
    case TemplateStateActions.addPromptOutputProperty: {
      if (!state) return state;
      const propertyId = `output-${generateUniqueId()}`;

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const currentPropertiesOrder = p.promptOutput.propertiesOrder || [];
              //if there is no saved order - use the order of the properties
              const propertiesOrder = currentPropertiesOrder.length
                ? currentPropertiesOrder
                : Object.keys(p.promptOutput.properties).reduce(
                    (acc, key) => [...acc, key],
                    [] as string[],
                  );
              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: {
                    ...p.promptOutput.properties,
                    [propertyId]: {
                      id: propertyId,
                      type: 'string',
                      description: '',
                    },
                  },
                  propertiesOrder: [...propertiesOrder, propertyId],
                },
              };
            } else {
              return p;
            }
          }),
        },
      };
      break;
    }
    case TemplateStateActions.editPromptOutputProperty: {
      if (!state) return state;

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const currentPropertiesOrder = p.promptOutput.propertiesOrder || [];
              //if there is no saved order - use the order of the properties
              const propertiesOrder = action.promptOutputPropertyOrder
                ? action.promptOutputPropertyOrder
                : currentPropertiesOrder.length
                ? currentPropertiesOrder
                : Object.keys(p.promptOutput.properties).reduce(
                    (acc, key) => [...acc, key],
                    [] as string[],
                  );

              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: {
                    ...p.promptOutput.properties,
                    [action.promptOutputProperty.id]: action.promptOutputProperty,
                  },
                  propertiesOrder,
                },
              };
            } else {
              return p;
            }
          }),
        },
      };
      break;
    }
    case TemplateStateActions.deletePromptOutputProperty: {
      if (!state) return state;
      const prompt = (state.aiContent?.prompts || []).find(p => p.id === action.promptId);
      if (!prompt) throw new Error('No prompt found');

      const promptOutput = { ...prompt.promptOutput };

      delete promptOutput.properties[action.promptOutputPropertyId];

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          prompts: (state.aiContent?.prompts || []).map(p => {
            if (p.id === action.promptId) {
              const propertiesOrder = promptOutput.propertiesOrder?.filter(
                id => id !== action.promptOutputPropertyId,
              );

              return {
                ...p,
                promptOutput: {
                  ...p.promptOutput,
                  properties: {
                    ...promptOutput.properties,
                  },
                  propertiesOrder: propertiesOrder?.length ? propertiesOrder : undefined,
                },
              };
            } else {
              return p;
            }
          }),
        },
      };
      break;
    }
    case TemplateStateActions.editGeneratorCategories: {
      if (!state) return state;

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          categories: action.categories,
        },
      };
      break;
    }

    case TemplateStateActions.convertTextElementToAITextElement: {
      if (!state) return state;

      const currentSlide = state.content.slides?.find(
        r => r.id === state.currentRound,
      ) as Slide;
      if (currentSlide.type === 'poll' && action.isPollOption) {
        const pollOptions = currentSlide.pollOptions?.map(pollOption => {
          if (pollOption.id === action.elementId) {
            const blocks = parseStringToSlateObject(pollOption.displayName);

            const firstParagraph = blocks.find(b => b.type === 'paragraph');
            const textChild = firstParagraph?.children?.[0];
            const textColor = textChild
              ? isCuripodText(textChild)
                ? textChild.color
                : firstParagraph?.color || theme.colors.black
              : firstParagraph?.color || theme.colors.black;
            const newParagraph = createSimpleSlateParagraphFromAIOutput({
              text: action.exampleText,
              align: firstParagraph?.align || 'left',
              color: textColor,
              size: firstParagraph?.size || 40,
            });
            return {
              ...pollOption,
              displayName: JSON.stringify([newParagraph]),
              promptSourceId: action.promptSourceId,
            };
          }
          return pollOption;
        });
        currentSlide.pollOptions = pollOptions;
      }
      const slideElements: SlideElement[] = currentSlide.elements.map(e => {
        if (e.id === action.elementId) {
          const blocks = parseStringToSlateObject(e.textValue);

          const firstParagraph = blocks.find(b => b.type === 'paragraph');
          const textChild = firstParagraph?.children?.[0];
          const textColor = textChild
            ? isCuripodText(textChild)
              ? textChild.color
              : firstParagraph?.color || theme.colors.black
            : firstParagraph?.color || theme.colors.black;

          const newParagraph = createSimpleSlateParagraphFromAIOutput({
            text: action.exampleText,
            align: firstParagraph?.align || 'left',
            color: textColor,
            size: firstParagraph?.size || 40,
          });

          return {
            ...e,
            type: 'generatorText',
            promptSourceId: action.promptSourceId,
            textValue: JSON.stringify([newParagraph]),
          };
        }
        return e;
      });
      currentSlide.elements = slideElements;

      const slides = [...(state.content.slides || [])];

      const newSlides = slides
        ? slides.map(s => (s.id === currentSlide.id ? currentSlide : s))
        : null;
      newState = {
        ...state,
        content: { ...state.content, slides: newSlides },
      };
      break;
    }
    case TemplateStateActions.convertImageElementToAIImageElement: {
      if (!state) return state;

      const currentSlide = state.content.slides?.find(
        r => r.id === state.currentRound,
      ) as Slide;

      const slideElements: SlideElement[] = currentSlide.elements.map(e => {
        if (e.id === action.elementId) {
          return {
            ...e,
            type: 'generatorImage',
            promptSourceId: action.promptImageSourceId,
            imageObjectFit: 'cover',
            mediaFileId: action.exampleImage.id,
            mediaMimetype: action.exampleImage.mimetype,
            mediaUrl: action.exampleImage.url, // url image support
            mediaThumbnailUrl: action.exampleImage.thumbnail, // url image support
            mediaCropConfig: undefined, // we never crop an ai generated image (yet)
            mediaAltText: undefined, // we don't have alt Text for ai generated images
          };
        }
        return e;
      });
      currentSlide.elements = slideElements;

      const slides = [...(state.content.slides || [])];

      const newSlides = slides
        ? slides.map(s => (s.id === currentSlide.id ? currentSlide : s))
        : null;
      newState = {
        ...state,
        content: { ...state.content, slides: newSlides },
      };
      break;
    }

    case TemplateStateActions.changeSubjects: {
      if (!state) return state;

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          subjects: action.subjects,
        },
      };
      break;
    }

    case TemplateStateActions.changeGrades: {
      if (!state) return state;

      newState = {
        ...state,
        aiContent: {
          ...(state.aiContent || emptyAIContent),
          grades: action.grades,
        },
      };
      break;
    }
    case TemplateStateActions.addCurifyPlaceholderSlides: {
      if (!state) return state;

      const promptInput = state.aiContent?.promptInputs.find(
        p => p.id === action.promptInputId,
      );

      if (!promptInput) return state;

      const newSlides = createCurifyPlaceholderSlides(
        promptInput,
        state.content.slides || [],
      );

      newState = {
        ...state,
        content: {
          ...state.content,
          slides: newSlides,
        },
      };
      break;
    }
    case TemplateStateActions.attachmentUpserted: {
      if (!state) return state;

      const exists = (state.attachments || []).some(
        a => a.attachmentId === action.attachment.attachmentId,
      );

      const newAttachments = exists
        ? state.attachments
        : [...(state.attachments || []), action.attachment];

      newState = {
        ...state,
        attachments: newAttachments,
      };
      break;
    }
    case TemplateStateActions.attachmentCreated: {
      if (!state) return state;

      newState = {
        ...state,
        attachments: [...(state.attachments || []), action.attachment],
      };
      break;
    }
    case TemplateStateActions.attachmentEdited: {
      if (!state) return state;

      newState = {
        ...state,
        attachments: (state.attachments || []).map(a =>
          a.attachmentId === action.attachment.attachmentId ? action.attachment : a,
        ),
      };
      break;
    }
    case TemplateStateActions.attachmentDeleted: {
      if (!state) return state;

      /* When we remove an attachment from a lesson we remove the attachment from 
        the lesson itself. We do not delete the attachment file, as that can be reused by other lessons as a generic file upload.
        Then we remove all the element shortcuts that references this attachment
      */
      newState = {
        ...state,
        content: {
          ...state.content,
          slides: (state.content?.slides || []).map(slide => ({
            ...slide,
            elements: slide.elements.filter(e => e.attachmentId !== action.attachmentId),
          })),
        },
        attachments: (state.attachments || []).filter(
          a => a.attachmentId !== action.attachmentId,
        ),
      };
      break;
    }

    default:
      newState = state;
      break;
  }
  if (newState) {
    // Make sure we always update modifiedAt so that saving works as expected
    newState.modifiedAt = new Date().getTime();
  }
  return newState;
}

type DesignerDataStateContextType = {
  state: {
    localTemplate: TemplateState;
    isFetchingTemplate: boolean;
    canUndo: boolean;
    canRedo: boolean;
  };
  dispatch: Dispatch<TemplateAction>;
  handleUndo: () => void;
  handleRedo: () => void;
  handleKeyBoardCopy: () => void;
  handleKeyBoardCut: () => void;
  handleKeyBoardPaste: () => void;
  handleKeyBoardDelete: () => void;
  handleKeyBoardDuplicate: () => void;
  handleExit: () => void;
  handleSlideChange: (slide: Slide) => void;
  addSlide: (newSlides: Slide[], replace?: boolean) => void;
  addTranslatedSlides: (
    newSlidesAndAttachments: { slides: Slide[]; lessonAttachments: LessonAttachment[] },
    roundsToTranslate: {
      name: string;
      slideId: string;
    }[],
  ) => void;
  handleAllSlideChange: (slides: Slide[], selectedSlideId: string) => void;
  handleSlideTypeChanged: (
    combinedType: SlideType | SlideContentType,
    replace?: boolean | undefined,
  ) => void;
  handleSlideVariantDesignation: (slide: Slide) => void;
  handleElementsChange: ({
    index,
    zChange,
    updatedElements,
  }: OnElementsChangeProps) => void;
  handleElementsAdd: (
    newElements: SlideElement[],
    elementsToRemove?: SlideElement[],
  ) => void;
  handleElementsDelete: (element: SlideElement[]) => void;
  handleCreateNewSlide: (
    combinedType: SlideType | SlideContentType | 'blank',
    replace?: boolean | undefined,
  ) => void;
  onSlideSelected: (slide: Slide) => void;
  handleSlideDelete: (slide: Slide) => void;
  handleSlideCopy: (slide: Slide) => void;
  handleSelectedTabChange: (tabState: TabState) => void;
  handleExportAsPdf: ({
    onSuccess,
    title,
  }: {
    onSuccess: () => void;
    title: string;
  }) => void;
  handleLeftArrowKey: (evt: KeyboardEvent) => void;
  handleRightArrowKey: (evt: KeyboardEvent) => void;
  handleUpArrowKey: (evt: KeyboardEvent) => void;
  handleDownArrowKey: (evt: KeyboardEvent) => void;
};

export const DesignerDataStateContext = createContext<DesignerDataStateContextType>({
  state: {
    localTemplate: undefined,
    isFetchingTemplate: false,
    canUndo: false,
    canRedo: false,
  },
  dispatch: () => {
    /**/
  },
  handleUndo: () => {
    /**/
  },
  handleSlideVariantDesignation: () => {
    /**/
  },
  handleRedo: () => {
    /**/
  },
  handleKeyBoardCopy: () => {
    /**/
  },
  handleKeyBoardCut: () => {
    /**/
  },
  handleKeyBoardPaste: () => {
    /**/
  },
  handleKeyBoardDelete: () => {
    /**/
  },
  handleKeyBoardDuplicate: () => {
    /**/
  },
  handleExit: () => {
    /**/
  },
  handleSlideChange: () => {
    /**/
  },
  handleElementsChange: () => {
    /**/
  },
  addSlide: () => {
    /**/
  },
  addTranslatedSlides: () => {
    /**/
  },
  handleAllSlideChange: () => {
    /**/
  },
  handleElementsAdd: () => {
    /**/
  },
  handleSlideTypeChanged: () => {
    /**/
  },
  handleCreateNewSlide: () => {
    /**/
  },
  handleElementsDelete: () => {
    /**/
  },
  onSlideSelected: () => {
    /**/
  },
  handleSlideDelete: () => {
    /**/
  },
  handleSlideCopy: () => {
    /**/
  },
  handleSelectedTabChange: () => {
    /**/
  },
  handleExportAsPdf: ({}: { onSuccess: () => void }) => {
    /**/
  },
  handleLeftArrowKey: () => {
    /**/
  },
  handleRightArrowKey: () => {
    /**/
  },
  handleUpArrowKey: () => {
    /**/
  },
  handleDownArrowKey: () => {
    /**/
  },
});

export function useDesignerDataContext<T>(
  selector: ContextSelector<DesignerDataStateContextType, T>,
) {
  return useContextSelector(DesignerDataStateContext, selector);
}

export const DesignerDataStateProvider = DesignerDataStateContext.Provider;

const useDesignerLogic = (dispatch: Dispatch<DesignerUIStateAction>) => {
  const isAgentEnabled = useMixpanelFlag('agent');
  const metrics = useMetrics();
  const { router, tenantId, workspaceId, parentFolderId, templateId } = useRouterMatch();

  // Make sure the intercom launcher is hidden
  useHideIntercom();

  const { accessBadge: userAccessBadge } = useAccessBadge();

  const { canRedo, canUndo, redo, undo, addToUndoStack } = useUndoRedo<Template>();
  const { template, isFetchingTemplate, error: errorFetchingTemplate } = useRawTemplate(
    templateId,
    tenantId,
  );

  const isTemplateOwner = useMemo(() => {
    return template && userAccessBadge?.user.id === template?.creator.uid;
  }, [userAccessBadge, template]);

  // Use template access handler to manage public template access
  const { accessError, publicTemplate } = useTemplateAccessHandler({
    templateId,
    isTemplateOwner,
    privateTemplateError: errorFetchingTemplate,
    template,
    isFetchingTemplate,
  });

  // Show error modal when needed
  const showTemplateAccessErrorModal = useDesignerUIContext(
    c => c.showTemplateAccessErrorModal,
  );

  // Show copy lesson modal when needed
  const showCopyLessonModal = useDesignerUIContext(c => c.showCopyLessonModal);

  useEffect(() => {
    if (accessError) {
      showTemplateAccessErrorModal(accessError);
    }
  }, [accessError, showTemplateAccessErrorModal]);

  // Show copy lesson modal when public template is available but user is not owner
  useEffect(() => {
    if (isTemplateOwner === true) {
      return;
    }

    if (!!publicTemplate && userAccessBadge !== undefined) {
      showCopyLessonModal();
    }
  }, [publicTemplate, isTemplateOwner, showCopyLessonModal, userAccessBadge]);

  const historyMiddleware = useCallback(
    (action: TemplateAction, state: TemplateState) => {
      if (!action.skipHistory && state) {
        addToUndoStack(state);
      }
    },
    [addToUndoStack],
  );

  const selectedElements = useDesignerUIContext(c => c.state.selectedElements);
  const setSelectedElements = useDesignerUIContext(c => c.setSelectedElements);
  const deselectElements = useDesignerUIContext(c => c.deselectElements);
  const zoom = useDesignerUIContext(c => c.state.zoom);
  const isDesktop = useDesignerUIContext(c => c.state.isDesktop);
  const forceLoadingState = useDesignerUIContext(c => c.state.forceLoadingState);
  const agentIsComplete = useDesignerUIContext(c => c.state.agent.isComplete);
  const isBuildingInitialLesson = useDesignerUIContext(
    c => c.state.initialLessonBuilding?.isLoading,
  );

  const arrowKeyIsDisabled = useDesignerUIContext(
    c => c.state.isArrowKeyNavigationDisabled,
  );

  const [localTemplate, dispatchWithHistory] = useReducerWithMiddleware(
    templateReducer,
    !templateId
      ? createEmptyTemplate({ workspaceId, parentFolderId, tenantId })
      : undefined,
    [],
    [historyMiddleware],
  );

  useEffect(() => {
    // sync localtemplate after the first save
    if (localTemplate && localTemplate.id === NEW_TEMPLATE_PARAM && template?.id) {
      dispatchWithHistory({
        type: TemplateStateActions.init,
        template: {
          ...template,
          content: { ...localTemplate.content },
          currentRound: localTemplate.currentRound,
        },
      });
      return;
    }
  }, [localTemplate, template, dispatchWithHistory]);

  useEffect(() => {
    if (template && !localTemplate) {
      dispatchWithHistory({
        type: TemplateStateActions.init,
        template: template,
      });
      return;
    }

    if (template && !isFetchingTemplate && forceLoadingState === 'success') {
      dispatchWithHistory({
        type: TemplateStateActions.init,
        template: template,
      });

      dispatch({
        type: DesignerUIStateActions.setForceLoadingState,
        payload: undefined,
      });
    }

    // if you have created a game we need to update the localTemplate with the activeGame
    // to prevent duplicate save
    if (
      template?.content.activeGame &&
      localTemplate?.content &&
      !isEqual(template?.content.activeGame, localTemplate?.content.activeGame)
    ) {
      dispatchWithHistory({
        type: TemplateStateActions.initActiveGame,
        activeGame: template?.content.activeGame,
      });
    }
  }, [
    template,
    localTemplate,
    dispatchWithHistory,
    forceLoadingState,
    dispatch,
    isFetchingTemplate,
  ]);

  const selectedSlide = useMemo(() => {
    if (
      localTemplate?.content.slides &&
      localTemplate?.content.slides.length > 0 &&
      localTemplate?.currentRound
    ) {
      return localTemplate?.content.slides.find(
        r => r.id === localTemplate?.currentRound,
      );
    }
    return undefined;
  }, [localTemplate?.currentRound, localTemplate?.content.slides]);

  useEffect(() => {
    if (
      !selectedSlide &&
      localTemplate &&
      localTemplate.content.slides &&
      localTemplate.content.slides.length > 0
    ) {
      // Upon entering the creator if there is no selected round, set the first round as current round
      dispatchWithHistory({ type: TemplateStateActions.initSetFirstSlide });
    }
  }, [localTemplate, selectedSlide, dispatchWithHistory]);

  // Inject LOADING_SLIDE when initial lesson building starts and there are no slides
  useEffect(() => {
    if (
      isBuildingInitialLesson &&
      localTemplate &&
      (!localTemplate.content.slides || localTemplate.content.slides.length === 0)
    ) {
      // Add the LOADING_SLIDE to the template so it appears in the editor
      dispatchWithHistory({
        type: TemplateStateActions.addSlide,
        slides: [LOADING_SLIDE],
        replace: false,
        skipHistory: true,
      });
    }
  }, [isBuildingInitialLesson, localTemplate, dispatchWithHistory]);

  // Remove LOADING_SLIDE when initial lesson building completes
  useEffect(() => {
    if (
      !isBuildingInitialLesson &&
      localTemplate?.content.slides?.some(s => s.id === LOADING_SLIDE_ID)
    ) {
      // Remove the LOADING_SLIDE and select the first actual slide
      const slidesWithoutLoader = localTemplate.content.slides.filter(
        s => s.id !== LOADING_SLIDE_ID,
      );
      const firstSlide = slidesWithoutLoader[0];

      if (firstSlide) {
        dispatchWithHistory({
          type: TemplateStateActions.allSlidesChanged,
          slides: slidesWithoutLoader,
          selectedSlideId: firstSlide.id,
          skipHistory: true,
        });
      }
    }
  }, [isBuildingInitialLesson, localTemplate, dispatchWithHistory]);
  useEffect(() => {
    if (!selectedSlide) return;

    // Helper function to determine if a slide is an activity slide
    const isActivitySlide = (slideType: SlideType) => {
      return [
        'answers',
        'aiAssessedAnswers',
        'aiAssessedImageAnswers',
        'canvas',
        'wordcloud',
        'poll',
        'podium',
      ].includes(slideType);
    };

    const newSlideIsActivity = isActivitySlide(selectedSlide.type);

    // Always open the interactiveElements tab for activity slides on desktop
    if (newSlideIsActivity && isDesktop) {
      dispatch({
        type: DesignerUIStateActions.setSelectedTab,
        payload: { tab: 'interactiveElements' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlide?.id]); // Only trigger when slide ID changes, not when slide content changes

  // Handle tab management when lesson generation completes
  useEffect(() => {
    // Only handle this once when the agent completes during initial lesson building
    if (agentIsComplete && isBuildingInitialLesson) {
      if (!selectedSlide) return;

      // Open the interactiveElements tab when the lesson has been generated
      dispatch({
        type: DesignerUIStateActions.setSelectedTab,
        payload: { tab: 'interactiveElements' },
      });

      // Complete the initial lesson building flow
      dispatch({
        type: DesignerUIStateActions.completeInitialLessonBuilding,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentIsComplete, isBuildingInitialLesson, selectedSlide]);

  const handleUndo = useCallback(() => {
    const item = undo();
    if (item) {
      deselectElements();
      dispatchWithHistory({
        type: TemplateStateActions.init,
        skipHistory: true,
        template: item,
      });
    }
  }, [undo, deselectElements, dispatchWithHistory]);

  const handleRedo = useCallback(() => {
    const item = redo();
    if (item) {
      deselectElements();
      dispatchWithHistory({
        type: TemplateStateActions.init,
        skipHistory: true,
        template: item,
      });
    }
  }, [redo, deselectElements, dispatchWithHistory]);

  const handleSlideDelete = useCallback(
    (slide: Slide) => {
      dispatchWithHistory({
        type: TemplateStateActions.deleteSlide,
        slideId: slide.id,
      });
      metrics.logEvent('Creator.DeleteRound.Confirm', {
        slideId: slide.id,
        block: slide.blockLabel,
        blockId: slide.blockId,
        grade: localTemplate?.metadata?.grade,
        subject: localTemplate?.metadata?.subject,
        language: localTemplate?.language,
        templateId: templateId,
        skeleton: localTemplate?.metadata?.skeleton,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metrics, dispatchWithHistory],
  );

  const onSlideSelected = useCallback(
    (slide: Slide) => {
      deselectElements();
      dispatch({ type: DesignerUIStateActions.selectSlide, payload: slide });
      dispatchWithHistory({
        type: TemplateStateActions.changeSelectedSlide,
        slideId: slide.id,
      });
      // Tab management is handled by the useEffect above
    },
    [deselectElements, dispatch, dispatchWithHistory],
  );

  const handleExit = useCallback(() => {
    router.push(`/${tenantId}/home`);
  }, [router, tenantId]);

  const handleSlideCopy = useCallback(
    (slide: Slide) => {
      dispatchWithHistory({ type: TemplateStateActions.copySlide, slideId: slide.id });
    },
    [dispatchWithHistory],
  );

  const handleSlideChange = useCallback(
    (slide: Slide) => {
      const slideElements = slide.elements.filter(e => !e.isPollOption);
      dispatchWithHistory({
        type: TemplateStateActions.changeSlide,
        slide: { ...slide, elements: slideElements },
      });
    },
    [dispatchWithHistory],
  );

  const addSlide = useCallback(
    (newSlides: Slide[], replace = false) => {
      dispatchWithHistory({
        type: TemplateStateActions.addSlide,
        slides: newSlides,
        replace,
      });
      metrics.logEvent('Creator.AddSlide');
    },
    [dispatchWithHistory, metrics],
  );
  const addTranslatedSlides = useCallback(
    (
      newSlidesAndAttachments: { slides: Slide[]; lessonAttachments: LessonAttachment[] },
      roundsToTranslate: {
        name: string;
        slideId: string;
      }[],
    ) => {
      dispatchWithHistory({
        type: TemplateStateActions.addTranslatedSlides,
        slides: newSlidesAndAttachments.slides,
        lessonAttachments: newSlidesAndAttachments.lessonAttachments,
        roundsToTranslate,
      });
      metrics.logEvent('Creator.AddTranslatedSlides');
    },
    [dispatchWithHistory, metrics],
  );

  const handleSlidesDragged = useCallback(
    (slides: Slide[]) => {
      dispatchWithHistory({ type: TemplateStateActions.slidesDragged, slides: slides });
    },
    [dispatchWithHistory],
  );

  const handleAllSlideChange = useCallback(
    (slides: Slide[], selectedSlideId: string) => {
      dispatchWithHistory({
        type: TemplateStateActions.allSlidesChanged,
        slides: slides,
        selectedSlideId: selectedSlideId,
      });
    },
    [dispatchWithHistory],
  );

  const setBackgroundForAllSlides = useCallback(
    (media: RoundMedia) => {
      dispatchWithHistory({ type: TemplateStateActions.setBackgroundOnAllSlides, media });
    },
    [dispatchWithHistory],
  );

  const handleSlideTypeChanged = useCallback(
    (combinedType: SlideType | SlideContentType, replace?: boolean) => {
      if (!selectedSlide || !localTemplate) return;
      if (isSlideType(combinedType) || combinedType === 'slide') {
        const newSlide = createNewEmptySlide({
          type: 'slide',
          contentType: combinedType as SlideContentType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements.filter(e => !e.isPollOption);
        newSlide.contextElements = [];
        newSlide.generatorId = selectedSlide.generatorId;
        addSlide([newSlide], replace ? true : isEmptySlide(selectedSlide));
        return;
      }
      if (combinedType === 'answers' || combinedType === 'canvas') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        const newVotingSlide = createNewEmptySlide({
          type: 'voting',
          textValue: `Most relevant ${
            combinedType === 'canvas' ? 'drawings' : 'answers'
          }`,
        });
        const newPodiumSlide = createNewEmptySlide({
          type: 'podium',
        });
        newVotingSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newVotingSlide.dataReferenceSlideId = newSlide.id;
        newVotingSlide.elements = selectedSlide.elements;
        newPodiumSlide.dataReferenceSlideId = newVotingSlide.id;
        newPodiumSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newPodiumSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newSlide.generatorId = selectedSlide.generatorId;
        addSlide(
          [newSlide, newVotingSlide, newPodiumSlide],
          replace ? true : isEmptySlide(selectedSlide),
        );
        return;
      }
      if (combinedType === 'aiAssessedAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        newSlide.generatorId = selectedSlide.generatorId;

        const newGridSlide = createNewEmptySlide({
          type: 'responsegrid',
        });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        newGridSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newGridSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newGridSlide.generatorId = selectedSlide.generatorId;
        addSlide([newSlide, newGridSlide], replace ? true : isEmptySlide(selectedSlide));
        return;
      }
      if (combinedType === 'aiAssessedImageAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        newSlide.generatorId = selectedSlide.generatorId;

        const newGridSlide = createNewEmptySlide({
          type: 'responsegrid',
        });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        newGridSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newGridSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newGridSlide.generatorId = selectedSlide.generatorId;
        addSlide([newSlide, newGridSlide], replace ? true : isEmptySlide(selectedSlide));
        return;
      }
      if (combinedType === 'wordcloud' || combinedType === 'poll') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
        });
        newSlide.backgroundMedia = selectedSlide.backgroundMedia;
        newSlide.elements = selectedSlide.elements;
        newSlide.contextElements = selectedSlide.contextElements;
        newSlide.generatorId = selectedSlide.generatorId;
        addSlide([newSlide], replace ? true : isEmptySlide(selectedSlide));
      }
    },
    [addSlide, localTemplate, selectedSlide],
  );

  const handleCreateNewSlide = useCallback(
    (combinedType: SlideType | SlideContentType | 'blank', replace?: boolean) => {
      if (!localTemplate) return;
      deselectElements();
      if (combinedType === 'blank') {
        const newSlide = createNewEmptySlide({ type: 'slide' });
        addSlide([newSlide], false);

        setSelectedTab(dispatch, {
          tab: 'addActivity',
          subTab: 'newBlankSlide',
        });

        return;
      }
      if (isSlideType(combinedType) || combinedType === 'slide') {
        const newSlide = createNewEmptySlide({
          type: 'slide',
          contentType: combinedType as SlideContentType,
          textValue: 'A curipod slide',
          textColor: theme.colors.blackPure,
          includeMedia: true,
        });

        addSlide([newSlide], replace ? true : isEmptySlide(selectedSlide));
        if (combinedType === 'fullscreen_media') {
          // Select the media element
          setSelectedElements([newSlide.elements[0]]);
        }
        return;
      }
      if (combinedType === 'answers' || combinedType === 'canvas') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          includeMedia: false,
          textColor: theme.colors.blackPure,
          textValue:
            combinedType === 'answers'
              ? 'Write your question'
              : 'Write drawing instructions',
        });
        const newVotingSlide = createNewEmptySlide({
          type: 'voting',
          textColor: theme.colors.blackPure,
          textValue: `Most relevant ${
            combinedType === 'canvas' ? 'drawings' : 'answers'
          }`,
          includeMedia: false,
        });
        const newPodiumSlide = createNewEmptySlide({
          type: 'podium',
          includeMedia: false,
        });
        newVotingSlide.dataReferenceSlideId = newSlide.id;
        newPodiumSlide.dataReferenceSlideId = newVotingSlide.id;

        addSlide(
          [newSlide, newVotingSlide, newPodiumSlide],
          replace || isEmptySlide(selectedSlide),
        );
        return;
      }
      if (combinedType === 'aiAssessedAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue: 'Write your question (AI feedback)',
          includeMedia: false,
        });

        const newGridSlide = createNewEmptySlide({
          type: 'responsegrid',
        });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        addSlide([newSlide, newGridSlide], replace ? true : isEmptySlide(selectedSlide));
        return;
      }
      if (combinedType === 'aiAssessedImageAnswers') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue: 'Write drawing instructions (AI feedback)',
          includeMedia: false,
        });

        const newGridSlide = createNewEmptySlide({
          type: 'responsegrid',
        });
        newGridSlide.dataReferenceSlideId = newSlide.id;
        addSlide([newSlide, newGridSlide], replace ? true : isEmptySlide(selectedSlide));
        return;
      }
      if (combinedType === 'wordcloud' || combinedType === 'poll') {
        const newSlide = createNewEmptySlide({
          type: combinedType as SlideType,
          textColor: theme.colors.blackPure,
          textValue:
            combinedType === 'poll'
              ? 'Write a poll question'
              : 'Write a word cloud topic',
          includeMedia: false,
        });

        addSlide([newSlide], replace ? true : isEmptySlide(selectedSlide));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addSlide, localTemplate, isAgentEnabled],
  );

  const elementsToRender = useMemo(() => {
    if (!selectedSlide) return [];
    if (selectedSlide.type === 'poll') {
      const pollElements = createPollElements({
        pollOptions: selectedSlide.pollOptions || [],
        aspect: SLIDE_ASPECT,
      });
      return [...selectedSlide.elements, ...pollElements];
    }
    return selectedSlide.elements;
  }, [selectedSlide]);

  const handleElementsAdd = useCallback(
    (newElements: SlideElement[], elementsToRemove?: SlideElement[]) => {
      if (!selectedSlide) return;

      if (newElements.some(e => e.isPollOption)) return;
      const existingElements = selectedSlide?.elements || [];
      const newSlide: Slide = {
        ...selectedSlide,
        elements: [...existingElements, ...newElements].filter(
          e => !elementsToRemove?.some(el => el.id === e.id),
        ),
      };
      handleSlideChange(newSlide);
      setSelectedElements(newElements);
    },
    [handleSlideChange, selectedSlide, setSelectedElements],
  );
  const handleElementsDelete = useCallback(
    (elements: SlideElement[]) => {
      if (!selectedSlide) return;
      if (elements?.some(e => e?.lockedFromDeletion)) return;

      // Additional check to prevent deletion of question context elements
      if (
        elements?.some(e =>
          selectedSlide.contextElements?.some(
            ce => ce.purpose === 'question' && ce.elementId === e.id,
          ),
        )
      )
        return;

      const deletedElements = elements.filter(e => !e?.isPollOption);
      const deletedPollOption = elements.filter(e => e?.isPollOption);

      // updates with the new elements
      const newElements = [...selectedSlide.elements].filter(
        e => !deletedElements.some(el => el.id === e.id),
      );
      const newContextElements = [...selectedSlide.contextElements].filter(
        e => !deletedElements.some(el => el.id === e.elementId),
      );

      let newSlide: Slide = {
        ...selectedSlide,
        elements: newElements,
        contextElements: newContextElements,
      };
      if (deletedPollOption.length > 0 && selectedSlide.pollOptions) {
        const firstPollOption = selectedSlide.pollOptions?.[0];
        const newPollOptions = [...(selectedSlide.pollOptions || [])].filter(
          p => !deletedPollOption.some(el => el.id === p.id),
        );

        // should not be able to delete the last element
        newSlide = {
          ...newSlide,
          pollOptions: newPollOptions.length > 0 ? newPollOptions : [firstPollOption],
        };
      }

      for (const element of elements) {
        if (selectedSlide.type === 'canvas' && element.type === 'image') {
          const isDeletedElementBackgroundImagePreview = selectedSlide.contextElements.some(
            el => el.purpose === 'drawingBackground' && el.elementId === element.id,
          );
          if (isDeletedElementBackgroundImagePreview) {
            newSlide.backgroundDrawingFileId = undefined;
            newSlide.backgroundDrawingConfigFiles = undefined;
          }
        }
      }
      handleSlideChange(newSlide);
      deselectElements();
    },
    [deselectElements, handleSlideChange, selectedSlide],
  );

  const handleElementsChange = useCallback(
    ({
      zChange,
      updatedElements,
      slide,
      skipUpdateSelectedElements,
    }: OnElementsChangeProps) => {
      const slideToUse = slide || selectedSlide;
      if (!slideToUse) return;

      // updates layer of the elements
      if (zChange && updatedElements) {
        let itemsCopy = [...slideToUse.elements];

        switch (zChange) {
          case 'up':
          case 'down':
            const move = zChange === 'down' ? -1 : 1;

            for (const updatedElement of updatedElements) {
              const index = itemsCopy.findIndex(item => item.id === updatedElement.id);

              if (index !== -1) {
                const targetIndex = index + move;

                if (targetIndex >= 0 && targetIndex < itemsCopy.length) {
                  // Swap the current item with the target item
                  [itemsCopy[index], itemsCopy[targetIndex]] = [
                    itemsCopy[targetIndex],
                    itemsCopy[index],
                  ];
                }
              }
            }
            break;

          case 'back':
            const frontItems = updatedElements.sort(
              (a, b) =>
                itemsCopy.findIndex(item => item.id === a.id) -
                itemsCopy.findIndex(item => item.id === b.id),
            );
            itemsCopy = frontItems.concat(
              itemsCopy.filter(
                item => !frontItems.find(updatedItem => updatedItem.id === item.id),
              ),
            );
            break;

          case 'front':
            const backItems = updatedElements.sort(
              (a, b) =>
                itemsCopy.findIndex(item => item.id === a.id) -
                itemsCopy.findIndex(item => item.id === b.id),
            );
            itemsCopy = itemsCopy
              .filter(item => !backItems.find(updatedItem => updatedItem.id === item.id))
              .concat(backItems);
            break;
        }

        const newSlide: Slide = {
          ...slideToUse,
          elements: itemsCopy,
        };
        handleSlideChange(newSlide);
        return;
      }

      if (updatedElements) {
        let newPollOptions = slideToUse.pollOptions;
        const newElements = [...slideToUse.elements].map(e => {
          const updatedElement = updatedElements.find(el => el.id === e.id);
          if (updatedElement) {
            return updatedElement;
          }
          return e;
        });

        // updates the poll options if the element is a poll option
        const updatesPollOptions = updatedElements.filter(e => e.isPollOption);
        if (updatesPollOptions.length > 0) {
          newPollOptions = [...(slideToUse.pollOptions || [])].map(oldPollOption => {
            const updatedPollOption = updatesPollOptions.find(
              el => el.id === oldPollOption.id,
            );
            if (updatedPollOption) {
              return {
                ...oldPollOption,
                displayName: updatedPollOption.textValue || oldPollOption.displayName,
                promptSourceId:
                  updatedPollOption.promptSourceId || oldPollOption.promptSourceId,
                ...(updatedPollOption.templatePlaceholderId && {
                  templatePlaceholderId: updatedPollOption.templatePlaceholderId,
                }),
              };
            }
            return oldPollOption;
          });
        }
        const newSlide: Slide = {
          ...slideToUse,
          elements: newElements,
          pollOptions: newPollOptions,
        };
        handleSlideChange(newSlide);
        // updates the selected elements if they are part of the new elements
        if (selectedElements && !skipUpdateSelectedElements) {
          const newSelectedElements = selectedElements.map(e => {
            const updatedElement = newElements.find(el => el.id === e.id);
            if (updatedElement) {
              return updatedElement;
            }
            return e;
          });

          setSelectedElements(newSelectedElements);
        }
        return;
      }
    },
    [handleSlideChange, selectedElements, selectedSlide, setSelectedElements],
  );

  const handleKeyBoardDelete = useCallback(() => {
    if (!selectedSlide || !localTemplate?.content.slides) {
      return;
    }
    if (selectedElements) {
      handleElementsDelete(selectedElements);
      metrics.logEvent('Creator.KeyboardDelete', { type: 'element' });
      return;
    }
    metrics.logEvent('Creator.KeyboardDelete', { type: 'slide' });
    handleSlideDelete(selectedSlide);
  }, [
    handleElementsDelete,
    handleSlideDelete,
    localTemplate?.content.slides,
    metrics,
    selectedElements,
    selectedSlide,
  ]);

  const handleAddPollOption = () => {
    if (!selectedSlide) {
      return;
    }
    const options = (selectedSlide.pollOptions || []) as PollOptions[];
    if (options.length < MAX_POLL_OPTIONS) {
      const existingColors = options.map(o => o.color).filter(notUndefined);
      const lastOptionSlateObject = getFirstSlateElementPropsFromSlateString(
        options?.[options.length - 1]?.displayName,
      );

      generateRoundOptionColor(existingColors || []);
      const newOption: PollOptions = {
        id: generateUniqueId().toString(),
        displayName: JSON.stringify([
          {
            type: 'paragraph',
            children: [
              {
                text: `Option ${options.length + 1}`,
                color: lastOptionSlateObject?.color || theme.colors.black,
              },
            ],
            align: 'center',
            size: 50,
          },
        ]),
        color: generateRoundOptionColor(existingColors || []),
      };
      const pollOptions = [...options, newOption];
      handleSlideChange({ ...selectedSlide, pollOptions });
      deselectElements();
    }
  };

  const handleKeyBoardDuplicate = useCallback(
    (metricsEvent?: string) => {
      if (!selectedSlide || !localTemplate?.content.slides) {
        return;
      }
      if (selectedElements) {
        if (selectedElements.every(e => e.isPollOption)) {
          handleAddPollOption();
          metrics.logEvent(metricsEvent ? metricsEvent : 'Creator.KeyboardDuplicate', {
            type: 'element',
          });
          return;
        }

        const duplicateElements = selectedElements
          .filter(e => !e.isPollOption)
          .map(e => {
            const duplicateElement = JSON.parse(JSON.stringify(e));
            duplicateElement.id = generateUniqueId().toString();
            duplicateElement.x = duplicateElement.x * 1.1;
            duplicateElement.y = duplicateElement.y * 1.1;
            duplicateElement.lockedFromDeletion = false;
            duplicateElement.lockedPosition = false;
            return duplicateElement;
          });

        handleElementsAdd(duplicateElements);
        metrics.logEvent('Creator.KeyboardDuplicate', { type: 'element' });
        return;
      } else {
        // If the user has no element select, we duplicate the slide
        dispatchWithHistory({
          type: TemplateStateActions.copySlide,
          slideId: selectedSlide.id,
        });
        metrics.logEvent('Creator.KeyboardDuplicate', { type: 'slide' });
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatchWithHistory,
      handleElementsAdd,
      localTemplate?.content.slides,
      metrics,
      selectedElements,
      selectedSlide,
    ],
  );
  const handleKeyBoardCopy = useCallback(async () => {
    // Can only copy when we have data and a selected round;

    if (!selectedSlide || !localTemplate?.content.slides) {
      return;
    }
    if (selectedElements) {
      await copyElement({ elements: selectedElements, slideId: selectedSlide.id });
      metrics.logEvent('Creator.KeyboardCopy', { type: 'element' });
      return;
    }
    metrics.logEvent('Creator.KeyboardCopy', { type: 'slide' });
    if (selectedSlide.type === 'slide') {
      // Selected round is the slide, so we copy that
      await copySlide({
        slides: [selectedSlide],
        tenantId,
        templateId: localTemplate.id,
        attachments: localTemplate.attachments,
      });
    } else {
      // Make sure we copy the activity rounds
      const relatedSlides = findRelatedSlidesWithoutIndex(
        selectedSlide.id,
        localTemplate.content.slides,
      );
      await copySlide({
        slides: relatedSlides,
        tenantId,
        templateId: localTemplate.id,
        attachments: localTemplate.attachments,
      });
    }
  }, [selectedSlide, localTemplate, selectedElements, metrics, tenantId]);

  const handleKeyBoardCut = useCallback(async () => {
    // Can only copy when we have data and a selected round;
    if (!selectedSlide || !localTemplate?.content.slides) {
      return;
    }
    if (selectedElements) {
      await copyElement({ elements: selectedElements, slideId: selectedSlide.id });
      handleElementsDelete(selectedElements);
      return;
    }

    if (selectedSlide.type === 'slide') {
      // Selected round is the slide, so we copy that
      await copySlide({
        slides: [selectedSlide],
        tenantId,
      });
      metrics.logEvent('Creator.KeyboardCut', { type: 'element' });
      return;
    } else {
      // Make sure we copy the activity rounds
      const relatedSlides = findRelatedSlidesWithoutIndex(
        selectedSlide.id,
        localTemplate.content.slides,
      );
      await copySlide({ slides: relatedSlides, tenantId });
      handleSlideDelete(selectedSlide);
      metrics.logEvent('Creator.KeyboardCut', { type: 'slide' });
      return;
    }
  }, [
    selectedSlide,
    localTemplate?.content.slides,
    selectedElements,
    handleElementsDelete,
    tenantId,
    metrics,
    handleSlideDelete,
  ]);
  const { upload } = useGameMediaFileUploader(tenantId);
  const handleUploadCuripodSvg = useCallback(
    (imgData: string) => {
      const convertedSvg = convertToCuripodSvg(imgData);
      const blob = new Blob([convertedSvg.curipodSvg], {
        type: 'image/svg+xml',
      });
      const svgFile = new File([blob], 'image.svg', { type: 'image/svg+xml' });
      if (svgFile) {
        readImageFiles(svgFile)
          .then(data => {
            return upload({
              imgData: data.src,
              fileId: curipodSvgId,
              svgText: convertedSvg.curipodSvg,
            });
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.error(e);
          });
      }

      const curipodSvgId = `${generateUniqueId().toString()}.svg`;

      let imageHeight = convertedSvg.height;
      let imageWidth = convertedSvg.width;

      const imageIsLargerThanSlide = imageHeight >= 1080 || imageWidth >= 1920;

      if (imageIsLargerThanSlide) {
        const imageRatio = imageWidth / imageHeight;
        const slideRatio = 1920 / 1080;
        if (imageRatio > slideRatio) {
          const newWidth = 1500;
          const newHeight = newWidth / imageRatio;
          imageHeight = newHeight;
          imageWidth = newWidth;
        } else {
          const newHeight = 900;
          const newWidth = newHeight * imageRatio;
          imageHeight = newHeight;
          imageWidth = newWidth;
        }
      }

      const newSvg = createFigureElement({
        height: imageHeight,
        width: imageWidth,
        defaultColors: convertedSvg.colors || undefined,
        mediaFileId: curipodSvgId,
      });
      handleElementsAdd([newSvg]);
      setSelectedElements([newSvg]);
    },
    [handleElementsAdd, setSelectedElements, upload],
  );

  const handleUploadPastedImage = useCallback(
    async (imgData: ClipboardItems) => {
      imgData.find(item => {
        const imageType = item.types.find(type =>
          type.startsWith('image/'),
        ) as MediaMimeType;

        if (imageType) {
          item.getType(imageType).then(blob => {
            const image = new Image();

            image.src = URL.createObjectURL(blob);
            // create temporary image to get the image size
            image.onload = () => {
              const imgFileId = `${generateUniqueId().toString()}`;
              const imgFile = new File([blob], imgFileId, { type: imageType });

              readImageFiles(imgFile)
                .then(data => {
                  return upload({
                    imgData: data.src,
                    fileId: imgFileId,
                  });
                })
                .catch(e => {
                  // eslint-disable-next-line no-console
                  console.error(e);
                });

              let imageHeight = image.naturalHeight;
              let imageWidth = image.naturalWidth;

              const imageIsLargerThanSlide = imageHeight >= 1080 || imageWidth >= 1920;

              if (imageIsLargerThanSlide) {
                const imageRatio = imageWidth / imageHeight;
                const slideRatio = 1920 / 1080;
                if (imageRatio > slideRatio) {
                  const newWidth = 1500;
                  const newHeight = newWidth / imageRatio;
                  imageHeight = newHeight;
                  imageWidth = newWidth;
                } else {
                  const newHeight = 900;
                  const newWidth = newHeight * imageRatio;
                  imageHeight = newHeight;
                  imageWidth = newWidth;
                }
              }

              const newImg = createImageElement({
                height: imageHeight,
                width: imageWidth,
                mediaMimeType: imageType,
                mediaFileId: imgFileId,
              });
              handleElementsAdd([newImg]);
              setSelectedElements([newImg]);
              URL.revokeObjectURL(image.src);
            };
          });
        }
      });
    },
    [handleElementsAdd, setSelectedElements, upload],
  );

  const handleKeyBoardPaste = useCallback(async () => {
    if (!localTemplate || !localTemplate.content.slides) {
      return;
    }

    const clipboardImage = await retrieveClipBoardImage();
    if (clipboardImage) {
      // If there's an image in the clipboard, initiate the upload process and continue.
      handleUploadPastedImage(clipboardImage);
    }
    const isImg =
      clipboardImage && clipboardImage[0].types.find(type => type.startsWith('image/'));
    if (!isImg) {
      // For non-image content, we check for SVG data and parse relevant data.
      const clipboardText = await retrieveClipBoardText();
      if (!clipboardText) return;
      if (isSvg(clipboardText)) {
        handleUploadCuripodSvg(clipboardText);
      }
      const parsedData = parseClipboardData(clipboardText);
      if (parsedData === undefined) return;
      if (!parsedData.value) return;
      if (!parsedData.value.payload) return;

      if (parsedData.value.type === 'elements' && selectedSlide) {
        const payload = parsedData.value.payload as {
          elements: SlideElement[];
          slideId?: string;
        };

        if (!payload) return;

        const newElements = payload.elements.map(newElement => {
          // make sure you can copy and paste element on the same place
          // if you copy to a new slide they contain the same placement,
          // if its on the same slide we move it a bit
          const newX =
            selectedSlide.id === payload.slideId ? newElement.x + 50 : newElement.x;

          const newY =
            selectedSlide.id === payload.slideId ? newElement.y + 50 : newElement.y;

          newElement.id = generateUniqueId().toString();
          newElement.x = newX;
          newElement.y = newY;
          newElement.lockedFromDeletion = false;
          newElement.lockedPosition = false;
          return newElement;
        });

        handleElementsAdd(newElements);
        setSelectedElements(newElements);
        return;
      }
      if (parsedData.value.type === 'slide') {
        if (parsedData.value.tenantId !== tenantId) return;

        const slideData = parsedData.value as {
          type: string;
          payload: Slide[];
          tenantId?: string;
          templateId?: string;
          attachments?: LessonAttachment[];
        };

        const newSlide = slideData.payload;
        const copiedAttachments = slideData.attachments;
        const sourceTemplateId = slideData.templateId;

        // Check if we're pasting into a different template
        const isDifferentTemplate =
          sourceTemplateId && sourceTemplateId !== localTemplate.id;

        if (isDifferentTemplate && copiedAttachments && copiedAttachments.length > 0) {
          // Map old attachment IDs to new/existing ones in this lesson
          const attachmentIdMap = new Map<string, string>();

          for (const attachment of copiedAttachments) {
            try {
              // Fetch the content from the source attachment (same as useAttachmentContent does)
              const attachmentContent = await BackendService.instance.gameFiles.fetchAttachmentContent(
                {
                  fileId: attachment.fileId,
                  isFromDiscover: false,
                },
              );

              const attachmentContentString = JSON.stringify(attachmentContent);

              // Check if an attachment with the same content already exists in this lesson
              let existingAttachment: LessonAttachment | undefined;

              for (const att of localTemplate.attachments || []) {
                try {
                  const existingContent = await BackendService.instance.gameFiles.fetchAttachmentContent(
                    {
                      fileId: att.fileId,
                      isFromDiscover: false,
                    },
                  );
                  const existingContentString = JSON.stringify(existingContent);

                  // Compare content strings and titles
                  if (
                    existingContentString === attachmentContentString &&
                    att.title === attachment.title
                  ) {
                    existingAttachment = att;
                    break;
                  }
                } catch (e) {
                  // Skip this attachment if we can't fetch its content
                  continue;
                }
              }

              if (existingAttachment) {
                // Reuse the existing attachment in this lesson
                attachmentIdMap.set(
                  attachment.attachmentId,
                  existingAttachment.attachmentId,
                );
              } else {
                // Create a new attachment on the backend with the content
                const newAttachment = await BackendService.instance.presentations.createAttachment(
                  {
                    title: attachment.title,
                    content: attachmentContentString,
                  },
                );

                // Map old ID to new ID
                attachmentIdMap.set(attachment.attachmentId, newAttachment.attachmentId);

                // Add the new attachment to this lesson (use upserted to ensure it's added)
                dispatchWithHistory({
                  type: TemplateStateActions.attachmentUpserted,
                  attachment: newAttachment,
                });
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error processing attachment:', error);
            }
          }

          // Update slide elements and prompts to reference the new/existing attachment IDs
          const slidesWithUpdatedAttachments = newSlide.map(slide => {
            // Update element attachment IDs
            const updatedElements = slide.elements?.map(element => {
              if (element.type === 'attachmentPassage' && element.attachmentId) {
                const newAttachmentId = attachmentIdMap.get(element.attachmentId);
                if (newAttachmentId) {
                  return { ...element, attachmentId: newAttachmentId };
                }
              }
              return element;
            });

            // Update prompt attachment references
            const updatedPrompts = slide.prompts?.map(prompt => {
              const updatedMessages = prompt.messages.map(message => {
                if (!message.content) return message;

                try {
                  const parsedContent = JSON.parse(message.content);
                  if (Array.isArray(parsedContent)) {
                    // This is Slate content, look for livePromptInput nodes
                    const updatedContent = parsedContent.map(node =>
                      updateSlateNodeAttachmentIds(node, attachmentIdMap),
                    );
                    return {
                      ...message,
                      content: JSON.stringify(updatedContent),
                    };
                  }
                } catch (e) {
                  // Content is not JSON, return as is
                }

                return message;
              });

              return {
                ...prompt,
                messages: updatedMessages,
              };
            });

            return {
              ...slide,
              elements: updatedElements,
              prompts: updatedPrompts,
            };
          });

          const newSlides = makeNewIdsForSlide(slidesWithUpdatedAttachments);
          addSlide(newSlides);
        } else {
          // Same template or no attachments - paste normally
          const newSlides = makeNewIdsForSlide(newSlide);
          addSlide(newSlides);
        }
        return;
      }
    }
    metrics.logEvent('Creator.KeyboardPaste');
  }, [
    localTemplate,
    metrics,
    handleUploadPastedImage,
    selectedSlide,
    handleUploadCuripodSvg,
    handleElementsAdd,
    setSelectedElements,
    tenantId,
    addSlide,
    dispatchWithHistory,
  ]);

  const isOnGoingGameModalVisible = useMemo(
    () => !!localTemplate?.content.activeGame?.isOngoing,
    [localTemplate],
  );

  // parses all elements to find all used colors
  const documentColors = useMemo(() => {
    if (!localTemplate?.content.slides) {
      return [];
    }
    const colors = localTemplate.content.slides.reduce((acc, slide) => {
      const slideColors = slide.elements.reduce((acc, element) => {
        if (element.type === 'svg' && element.colors) {
          return [...acc, ...element.colors];
        }
        if (element.type === 'shape' && element.colors) {
          return [...acc, ...element.colors];
        }

        if (element.type === 'text') {
          return [...acc, ...findAllSlateTextColors(element.textValue)];
        }

        return acc;
      }, [] as string[]);

      // adds the slide background color if it's a color
      if (slide.backgroundMedia?.fileMimeType === 'image/color') {
        return [...acc, ...slideColors, slide.backgroundMedia.fileId];
      }

      return [...acc, ...slideColors];
    }, [] as string[]);
    return uniq(colors);
  }, [localTemplate?.content.slides]);
  const handleSelectedTabChange = useCallback(
    (tabState: TabState) => {
      dispatch({ type: DesignerUIStateActions.setSelectedTab, payload: tabState });

      if (
        !selectedSlide &&
        localTemplate &&
        localTemplate.content.slides?.length === 0 &&
        (tabState.tab === 'background' ||
          tabState.tab === 'text' ||
          tabState.tab === 'media')
      ) {
        addSlide([createNewEmptySlide({ type: 'slide' })]);
      }
    },
    [addSlide, dispatch, selectedSlide, localTemplate],
  );

  const handleNewSlideClicked = useCallback(() => {
    addSlide([createNewEmptySlide({ type: 'slide' })]);
    dispatch({ type: DesignerUIStateActions.selectNewSlide });
  }, [addSlide, dispatch]);

  const handleExportAsPdf = useCallback(
    ({ onSuccess, title }: { onSuccess: () => void; title: string }) => {
      const allowedSlideTypes = [
        'wordcloud',
        'slide',
        'answers',
        'aiAssessedAnswers',
        'aiAssessedImageAnswers',
        'poll',
        'canvas',
      ];
      const slides = localTemplate?.content.slides ?? [];
      const slidesToBeExported = slides.filter(slide =>
        allowedSlideTypes.includes(slide.type),
      );

      const passagesToBeExported = localTemplate?.attachments;
      exportPdf({
        slides: slidesToBeExported,
        lessonAttachments: passagesToBeExported,
        fileName: `${title}.pdf`,
        onSuccess,
      });
    },
    [localTemplate?.content.slides, localTemplate?.attachments],
  );
  const handleElementMove = useCallback(
    ({ dx, dy }: { dx: number; dy: number }) => {
      if (!selectedElements) return;
      const newSelectedElements = selectedElements.map(e => {
        if (e.lockedPosition) return e;
        const x = e.x + dx / zoom;
        const y = e.y + dy / zoom;
        return { ...e, x, y };
      });
      handleElementsChange({ updatedElements: newSelectedElements });
    },
    [handleElementsChange, selectedElements, zoom],
  );
  const handleRightArrowKey = useCallback(
    (evt: KeyboardEvent) => {
      if (arrowKeyIsDisabled) return;
      evt.preventDefault();
      if (selectedElements && selectedElements.length > 0) {
        handleElementMove({ dx: 1, dy: 0 });
        return;
      }
      dispatchWithHistory({ type: TemplateStateActions.nextSlide });
    },
    [arrowKeyIsDisabled, dispatchWithHistory, handleElementMove, selectedElements],
  );
  const handleSlideVariantDesignation = useCallback(
    (slide: Slide) => {
      dispatchWithHistory({
        type: TemplateStateActions.changeSlide,
        slide,
      });
    },
    [dispatchWithHistory],
  );
  const handleLeftArrowKey = useCallback(
    (evt: KeyboardEvent) => {
      if (arrowKeyIsDisabled) return;
      evt.preventDefault();
      if (selectedElements && selectedElements.length > 0) {
        handleElementMove({ dx: -1, dy: 0 });
        return;
      }
      dispatchWithHistory({ type: TemplateStateActions.previousSlide });
    },
    [arrowKeyIsDisabled, dispatchWithHistory, handleElementMove, selectedElements],
  );
  const handleUpArrowKey = useCallback(
    (evt: KeyboardEvent) => {
      if (arrowKeyIsDisabled) return;
      evt.preventDefault();
      if (selectedElements && selectedElements.length > 0) {
        handleElementMove({ dx: 0, dy: -1 });
        return;
      }
      dispatchWithHistory({ type: TemplateStateActions.previousSlide });
    },
    [arrowKeyIsDisabled, dispatchWithHistory, handleElementMove, selectedElements],
  );
  const handleDownArrowKey = useCallback(
    (evt: KeyboardEvent) => {
      if (arrowKeyIsDisabled) return;
      evt.preventDefault();
      if (selectedElements && selectedElements.length > 0) {
        handleElementMove({ dx: 0, dy: 1 });
        return;
      }
      dispatchWithHistory({ type: TemplateStateActions.nextSlide });
    },
    [arrowKeyIsDisabled, dispatchWithHistory, handleElementMove, selectedElements],
  );

  return {
    localTemplate,
    isFetchingTemplate,
    localTemplateDispatch: dispatchWithHistory,
    handleSelectedTabChange,
    selectedSlide,
    arrowKeyIsDisabled,
    handleSlideChange,
    selectedElements,
    elementsToRender,
    setSelectedElements,
    handleAllSlideChange,
    handleElementsDelete,
    handleElementsChange,
    handleSlideVariantDesignation,
    handleCreateNewSlide,
    handleRightArrowKey,
    handleLeftArrowKey,
    handleDownArrowKey,
    handleUpArrowKey,
    handleSlideDelete,
    handleSlideCopy,
    handleSlidesDragged,
    handleSlideTypeChanged,
    handleKeyBoardCopy,
    handleElementsAdd,
    handleKeyBoardDelete,
    handleKeyBoardDuplicate,
    handleKeyBoardCut,
    handleKeyBoardPaste,
    setBackgroundForAllSlides,
    addSlide,
    addTranslatedSlides,
    handleExit,
    handleUndo,
    handleRedo,
    canRedo,
    canUndo,
    isOnGoingGameModalVisible,
    handleNewSlideClicked,
    documentColors,
    onSlideSelected,
    handleExportAsPdf,
  };
};

export const createEmptyTemplate = ({
  workspaceId,
  parentFolderId,
  tenantId,
}: {
  workspaceId?: string;
  parentFolderId?: string;
  tenantId: string;
}): Template => {
  return {
    id: NEW_TEMPLATE_PARAM,
    type: 'template',
    tenantId,
    content: {
      rounds: [],
      slides: [],
      gameIds: [],
    },
    creator: {
      uid: 'undefined',
    },
    isPrivate: workspaceId ? false : true,
    parentId: parentFolderId ? parentFolderId : workspaceId ? workspaceId : null,
    members: [],
    modifiedAt: new Date().getTime(),
    createdAt: new Date().getTime(),
    coverImageId: undefined,
  };
};
export default useDesignerLogic;
